import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { loadPublicReports } from '../../services/api';
import ReportModal from '../Reports/ReportModal';
import './ReportsSection.css';

function ReportsSection() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const fetchPublicReports = async () => {
      try {
        setLoading(true);
        const data = await loadPublicReports();
        
        // Convert the object to array and format for display
        const reportsArray = Object.values(data || {}).map(report => ({
          id: report.id,
          title: report.title,
          description: report.intro || report.tldr || 'No description available',
          date: report.date,
          timestamp: report.timestamp,
          css: report.css, // Include CSS content for modal
          intro: report.intro,
          tldr: report.tldr,
          image: `/assets/old/report_${report.title.toLowerCase()}.png`

        }));
        
        setReports(reportsArray);
        setError(null);
      } catch (err) {
        console.error('Error fetching public reports:', err);
        setError('Failed to load reports');
        // Fallback to static reports if API fails
        setReports([
          {
            id: 'crypto',
            title: 'Crypto',
            description: '',
            image: '/assets/old/report_crypto.png'
          },
          {
            id: 'derivatives',
            title: 'Derivatives',
            description: '',
            image: '/assets/old/report_derivatives.png'
          },
          {
            id: 'traditional',
            title: 'Traditional',
            description: '',
            image: '/assets/old/report_traditional.png'
          }
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchPublicReports();
  }, []);


  // Format date helper
  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // Modal handlers
  const handleReportClick = (report) => {
    setSelectedReport(report);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedReport(null);
  };

  if (loading) {
    return (
      <section className="home-reports-section">
        <div className="home-reports-container">
          <div className="home-reports-header">
            <div className="home-reports-label">Reports</div>
            <h2 className="home-reports-title">Preview recent reports</h2>
          </div>
          <div className="home-reports-loading">
            <p>Loading reports...</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="home-reports-section">
      <div className="home-reports-container">
        <div className="home-reports-header">
          <div className="home-reports-label">Reports</div>
          <h2 className="home-reports-title">Preview recent reports</h2>
        </div>
        {error && (
          <div className="home-reports-error">
            <p>{error}</p>
          </div>
        )}
        <div className="home-reports-grid">
          {reports.map((report) => (
            <button 
              key={report.id} 
              onClick={() => handleReportClick(report)}
              className="home-report-card"
            >
              <div className="home-report-image-container">
                <img 
                  src={report.image || '/assets/old/report_traditional.png'} 
                  alt={report.title}
                  className="home-report-image"
                />
              </div>
              <div className="home-report-content">
                <div className="home-report-date">
                  {formatDate(report.timestamp) || report.date || 'Recent'}
                </div>
                <h3 className="home-report-title">{report.title}</h3>
                <p className="home-report-description">{report.description}</p>
              </div>
            </button>
          ))}
        </div>
        
        {isModalOpen && createPortal(
          <ReportModal
            isOpen={isModalOpen}
            onClose={handleCloseModal}
            report={selectedReport}
            reportType="on_schedule"
          />,
          document.body
        )}
      </div>
    </section>
  );
}

export default ReportsSection;
